import { WabunkaExperienceListResponse } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience';

import { useRuntimeConfigKey, type UseRuntimeConfig } from '@/injectionKeys';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';
import { captureException } from '@/wabunka-src/common/utils/sentry';
import { buildExperienceDisplayPrice, type ExperienceDisplayPrice } from '@/wabunka-src/features/Search/models/experienceDisplayPrice';

// Algolia の price は料金マスタを見ず 0 になり得るため使わず、公開 API から詳細ページと同じ参考価格を取る（docs/Design Doc/キーワード検索.md）。
export const useExperienceDisplayPricesQuery = (slugs: Ref<string[]>) => {
  const { selectedCurrency } = useCurrency();

  const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
  const runtimeConfig = useRuntimeConfig();
  const baseURL = runtimeConfig.public.WABUNKA_PUBLIC_API_BASE_URL as string;

  const priceBySlug = ref<Record<string, ExperienceDisplayPrice>>({});
  const isLoading = ref(false);

  // 通貨切替 / hits 変更で stale な fetch を破棄するためのトークン
  let activeToken = 0;

  watch(
    [slugs, selectedCurrency],
    async ([currentSlugs, currentCurrency]) => {
      const token = ++activeToken;

      if (currentSlugs.length === 0) {
        priceBySlug.value = {};
        isLoading.value = false;
        return;
      }

      // 再取得の間は同じ通貨で取得済みの価格だけ残す（「もっと見る」で全体がちらつかないように）
      priceBySlug.value = Object.fromEntries(Object.entries(priceBySlug.value).filter(([, price]) => price.kind === 'private' || price.currency === currentCurrency));
      isLoading.value = true;
      try {
        const response = await $fetch('/experiences/search', {
          baseURL,
          headers: { Accept: 'application/json' },
          query: {
            slugs: currentSlugs.join(','),
            ...(currentCurrency === 'JPY' ? {} : { currency: currentCurrency }),
            perPage: '0', // 全件
          },
        });

        // この間に新しいリクエストが走っていた場合は結果を捨てる
        if (token !== activeToken) return;

        const parsed = WabunkaExperienceListResponse.fromJSON(response);
        const next: Record<string, ExperienceDisplayPrice> = {};
        for (const item of parsed.items ?? []) {
          const price = item.slug ? buildExperienceDisplayPrice(item, currentCurrency) : undefined;
          if (price) next[item.slug] = price;
        }
        priceBySlug.value = next;
      } catch (error) {
        if (token !== activeToken) return;
        // 取得失敗時は価格欄を出さない。UX 優先で Sentry には通知のみ
        captureException(error instanceof Error ? error : new Error(String(error)), 'wabunka');
        priceBySlug.value = {};
      } finally {
        if (token === activeToken) isLoading.value = false;
      }
    },
    { immediate: true },
  );

  return { priceBySlug, isLoading };
};
